import { useEffect, useState } from "react";
import { buildingById, decorationById } from "@zend/content";
import { GameIcon } from "../assets/gameIcons.js";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Button } from "../ui/Button.js";
import { Icon } from "../ui/Icon.js";
import { errorText } from "../base/state.js";
import { useSocial } from "../social/state.js";
import { allianceApi } from "./api.js";
import type { VisitSnapshot } from "./types.js";
import "./alliance.css";

/** `/app/visit/:accountId`: another player's base as a read-only layout map with a banner and Back (R77, D71). The 3D walk-through follows in the art phase. */
export function VisitView({ accountId }: { accountId: string }) {
  const t = useT();
  const openProfile = useSocial((s) => s.openProfile);
  const [snap, setSnap] = useState<VisitSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setSnap(null);
    setError(null);
    allianceApi.visit(accountId).then((s) => { if (alive) setSnap(s); }).catch((e: unknown) => { if (alive) setError(errorText(e)); });
    return () => { alive = false; };
  }, [accountId]);
  return (
    <div className="page visit" data-testid="visit-view">
      <header className="visit-banner glass">
        <Button variant="ghost" onClick={() => history.length > 1 ? history.back() : navigate("/app/alliance")} data-testid="visit-back"><Icon name="arrowLeft" /> {t("common.back")}</Button>
        <div className="grow" style={{ minWidth: 0 }}>
          <strong className="ellipsis" data-testid="visit-title">{snap ? t("alliance.visit.title", { name: snap.name }) : t("common.loading")}</strong>
          {snap ? <div className="hint">HQ {snap.hq}{snap.alliance ? ` · [${snap.alliance.tag}] ${snap.alliance.name}` : ""} · {snap.enclave.name}</div> : null}
        </div>
        {snap ? <Button onClick={() => void openProfile(snap.accountId)} data-testid="visit-profile"><Icon name="person" /></Button> : null}
      </header>
      {error ? <p role="alert" className="hint" style={{ padding: 12 }}>{error}</p> : null}
      {snap ? <LayoutMap snap={snap} /> : null}
    </div>
  );
}

/** The core grid as a flat map: every building with name and level, every decoration as a marker. */
function LayoutMap({ snap }: { snap: VisitSnapshot }) {
  const t = useT();
  const core = snap.grid.core;
  const open = snap.zones.filter((z) => z.state === "open").map((z) => z.rect);
  const minX = Math.min(core.x, ...open.map((r) => r.x)), minY = Math.min(core.y, ...open.map((r) => r.y));
  const maxX = Math.max(core.x + core.w, ...open.map((r) => r.x + r.w)), maxY = Math.max(core.y + core.h, ...open.map((r) => r.y + r.h));
  const cols = maxX - minX, rows = maxY - minY;
  return (
    <div className="visit-map glass" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, aspectRatio: `${cols} / ${rows}` }} data-testid="visit-map" data-buildings={snap.buildings.length}>
      <div className="visit-core" style={{ gridColumn: `${core.x - minX + 1} / span ${core.w}`, gridRow: `${core.y - minY + 1} / span ${core.h}` }} />
      {open.map((r, i) => <div key={i} className="visit-zone" style={{ gridColumn: `${r.x - minX + 1} / span ${r.w}`, gridRow: `${r.y - minY + 1} / span ${r.h}` }} />)}
      {snap.buildings.filter((b) => b.x >= 0 && b.w > 0).map((b) => (
        <div key={b.id} className={`visit-building state-${b.state}`} style={{ gridColumn: `${b.x - minX + 1} / span ${b.w}`, gridRow: `${b.y - minY + 1} / span ${b.h}` }} title={`${pick(buildingById(b.buildingId).name)} ${b.level}`} data-testid={`visit-building-${b.buildingId}`}>
          <GameIcon id={b.buildingId} size={22} />
          <span className="visit-label">{b.level}</span>
        </div>
      ))}
      {snap.decorations.map((d) => (
        <div key={d.id} className="visit-decoration" style={{ gridColumn: `${d.x - minX + 1} / span ${d.w ?? 1}`, gridRow: `${d.y - minY + 1} / span ${d.h ?? 1}` }} title={d.decoId ? pick(decorationById(d.decoId).name) : t("colony.garden")}>
          <GameIcon id={d.modelId} size={18} />
        </div>
      ))}
    </div>
  );
}
